import { useEffect, useState } from 'react'
import LanguageIcon from '@mui/icons-material/Language'
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state'
import Popover from '@mui/material/Popover'
import Button from '@mui/material/Button'
import { Grid, Theme, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const LanguageSwitcher = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  
  // Manage language state locally, synced with localStorage
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('weglot_current_lang') || 'en'
  })

  const languages = [
    { code: 'en', name: 'English', flag: 'us' },
    { code: 'es', name: 'Español', flag: 'es' },
    { code: 'pt-br', name: 'Português', flag: 'br' },
  ]

  // Sync with Weglot on mount and when language changes
  useEffect(() => {
    const syncWithWeglot = () => {
      if ((window as any).Weglot) {
        const currentWeglotLang = (window as any).Weglot.getCurrentLang()
        
        // If Weglot has a different language than our state, sync it
        if (currentWeglotLang !== lang) {
          console.log('LanguageSwitcher: Syncing Weglot from', currentWeglotLang, 'to', lang)
          ;(window as any).Weglot.switchTo(lang)
        }
      }
    }
    
    // Try immediately
    syncWithWeglot()
    
    // And after a delay to ensure Weglot is initialized
    const timer = setTimeout(syncWithWeglot, 500)
    
    return () => clearTimeout(timer)
  }, [lang])

  const getLanguageDisplay = (langCode: string) => {
    const language = languages.find(l => l.code === langCode)
    return language ? language.code.toUpperCase() : 'EN'
  }

  const handleLanguageChange = (newLang: string) => {
    console.log('LanguageSwitcher: Changing language to', newLang)
    
    // Update localStorage first
    localStorage.setItem('weglot_current_lang', newLang)
    
    // Map to app locale
    const langMap: { [key: string]: string } = {
      'en': 'en-us',
      'es': 'es-ES',
      'pt-br': 'pt-BR',
    }
    
    if (langMap[newLang]) {
      localStorage.setItem('locale', langMap[newLang])
    }
    
    // Update state
    setLang(newLang)
    
    // Update Weglot
    if ((window as any).Weglot) {
      (window as any).Weglot.switchTo(newLang)
      
      // Refresh page after a short delay to ensure translations are applied
      setTimeout(() => {
        window.location.reload()
      }, 100)
    }
  }

  return (
    <Grid item sx={{ mt: 1 }} >
      <PopupState variant="popover" popupId="language-switcher-popup">
        {(popupState) => (
          <div>
            <Button {...bindTrigger(popupState)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <LanguageIcon style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: theme.palette.text.secondary }} />

                {!isMobile && (
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      textTransform: 'none',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Language: <span style={{ color: theme.palette.primary.main, fontSize: '1rem' }}>{getLanguageDisplay(lang)}</span>
                  </Typography>
                )}
              </div>
            </Button>

            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
            >
              <div data-wg-notranslate style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => {
                      handleLanguageChange(language.code)
                      popupState.close()
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 10px',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      backgroundColor: lang === language.code ? '#f0f0f0' : 'transparent',
                    }}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${language.flag}.png`}
                      alt={`${language.name} flag`}
                      width="20"
                      height="15"
                    />
                    {language.name}
                  </button>
                ))}
              </div>
            </Popover>
          </div>
        )}
      </PopupState>
    </Grid>
  )
}

export default LanguageSwitcher